// src/components/dialogs/BuoysModal.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import Table, { buttonsStyle } from "../tables/table";
import {
  getBuoysByRegatta,
  postBuoy,
  putBuoy,
  deleteBuoy,
} from "../../../api/buoy";
import ConfirmDialog from "./ConfirmDialog";
import BuoyDialog from "./BuoyDialog";
import { alertError, alertSuccess } from "../../../utils/logger";

export default function BuoysModal({ show, onHide, regattaId, onBuoysChange }) {
  const [buoys, setBuoys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuoyDialog, setShowBuoyDialog] = useState(false);
  const [selectedBuoy, setSelectedBuoy] = useState(null);
  const [buoyToDelete, setBuoyToDelete] = useState(null);

  useEffect(() => {
    if (show && regattaId) {
      loadBuoys();
    }
  }, [show, regattaId]);

  const loadBuoys = () => {
    setLoading(true);
    getBuoysByRegatta(regattaId)
      .then((res) => {
        if (res.status === 200) {
          setBuoys(res.data);
          if (onBuoysChange) {
            onBuoysChange(regattaId, res.data.length);
          }
        }
      })
      .catch((err) =>
        alertError({ error: err, customMessage: "Could not load buoys" })
      )
      .finally(() => setLoading(false));
  };

  const handleSaveBuoy = async (buoy) => {
    try {
      if (buoy._id) {
        await putBuoy(buoy._id, buoy);
      } else {
        await postBuoy({ ...buoy, regatta: regattaId });
      }
      await loadBuoys();
    } catch (err) {
      alertError({ error: err, customMessage: "Could not save buoy" });
    } finally {
      setShowBuoyDialog(false);
      setSelectedBuoy(null);
    }
  };

  // arriba del array columns
  const formatCoord = (v) => {
    const n = Number(String(v).replace(",", ".")); // por si viniera con coma
    if (!Number.isFinite(n)) return "";
    // 6 decimales y quitamos ceros sobrantes
    return n.toFixed(6).replace(/\.?0+$/, "");
  };

  const columns = [
    { dataField: "fullName", text: "Name" },
    {
      text: "GPS / Coords",
      dataField: "gpsDevice", // usamos row en el formatter igualmente
      formatter: (gpsDevice, row) => {
        // 1) Si hay dispositivo, lo mostramos
        if (gpsDevice?.code) {
          return gpsDevice.phone
            ? `${gpsDevice.code} (${gpsDevice.phone})`
            : gpsDevice.code;
        }
        // 2) Si no hay dispositivo, intenta con coords
        const { latitude, longitude } = row || {};
        const latOk = latitude !== null && latitude !== undefined && latitude !== "";
        const lonOk = longitude !== null && longitude !== undefined && longitude !== "";
        if (latOk && lonOk) {
          const lat = formatCoord(latitude);
          const lon = formatCoord(longitude);
          return lat && lon ? (
            <a href={`https://maps.google.com/?q=${lat},${lon}`} target="_blank" rel="noopener noreferrer">
              {lat}, {lon}
            </a>
          ) : "–";
        }
        // 3) Nada disponible
        return "–";
      },
      sort: true,
      align: "center",
      headerAlign: "center",
    },
    {
      text: "Actions",
      formatter: (cell, row) => (
        <>
          <IconButton
            size="small"
            style={buttonsStyle}
            onClick={() => { setSelectedBuoy(row); setShowBuoyDialog(true); }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            style={buttonsStyle}
            onClick={() => { setBuoyToDelete(row); }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
      align: "center",
    },
  ];


  return (
    <>
      <Dialog open={show} onClose={onHide} fullWidth maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6">
              Buoys ({buoys.length})
            </Typography>
            <Button
              variant="contained"
              className="btn btn-primary"
              onClick={() => {
                setSelectedBuoy(null);
                setShowBuoyDialog(true);
              }}
            >
              Add buoy
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table data={buoys} columns={columns} keyField="_id" />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onHide} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {showBuoyDialog && (
        <BuoyDialog
          open={showBuoyDialog}
          onClose={() => {
            setShowBuoyDialog(false);
            setSelectedBuoy(null);
          }}
          onSave={handleSaveBuoy}
          buoy={selectedBuoy}
        />
      )}

      <ConfirmDialog
        open={!!buoyToDelete}
        setOpen={(v) => !v && setBuoyToDelete(null)}
        title="Are you sure you want to delete this buoy?"
        onConfirm={async () => {
          try {
            const res = await deleteBuoy(buoyToDelete._id);
            if (res.status === 204) {
              alertSuccess({
                title: "Deleted",
                customMessage: "Buoy deleted successfully",
              });

              const resBuoys = await getBuoysByRegatta(regattaId);
              if (resBuoys.status === 200) {
                setBuoys(resBuoys.data);
                if (onBuoysChange) {
                  onBuoysChange(regattaId, resBuoys.data.length);
                }
              }
            }
          } catch (err) {
            alertError({ error: err, customMessage: "Error deleting buoy" });
          } finally {
            await loadBuoys();
            setBuoyToDelete(null);
          }
        }}
      />
    </>
  );
}
